import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ContractBillToEditor } from "./ContractBillToEditor";
import type { BillToPartnerOption } from "./BillToPartnerPicker";

const ROLE_LABELS: Record<string, string> = {
  contracting_party: "Parte contratante",
  bill_to: "Facturar a",
  owner: "Propietario",
  operator: "Operador",
  promoter: "Promotor",
  main_contractor: "Contratista principal",
  end_customer: "Cliente final",
  representative: "Representante",
  other: "Otro",
};

interface PartnerRole {
  id: number;
  businessPartnerId: number;
  partnerCode: string;
  legalName: string;
  role: string;
  isPrimaryForRole: boolean;
  notes: string | null;
  validFrom: string | null;
  validUntil: string | null;
  finalizedAt: string | null;
}

interface Props {
  partnerRoles: PartnerRole[];
  contractId?: number;
  updatedAt?: string;
  canManageParties?: boolean;
  billingPartner?: BillToPartnerOption | null;
}

export function ContractPartiesSection({
  partnerRoles,
  contractId,
  updatedAt,
  canManageParties = false,
  billingPartner = null,
}: Props) {
  const active = partnerRoles.filter((r) => !r.finalizedAt && !r.validUntil);
  const primaryBillTo = active.find(r => r.role === "bill_to" && r.isPrimaryForRole);
  const currentBillTo = primaryBillTo
    ? { id: primaryBillTo.businessPartnerId, partnerCode: primaryBillTo.partnerCode, legalName: primaryBillTo.legalName }
    : billingPartner;
  const pendingBillTo = currentBillTo == null ? (
    <p role="status" className="rounded-md border p-3 text-sm text-muted-foreground">
      Facturar a: pendiente de configurar.
    </p>
  ) : null;
  const editor = canManageParties && contractId != null && updatedAt ? (
    <ContractBillToEditor contractId={contractId} updatedAt={updatedAt} currentValue={currentBillTo} />
  ) : null;

  if (active.length === 0) {
    return (
      <div className="space-y-3">
        {pendingBillTo}
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            No hay partes contractuales registradas.
          </CardContent>
        </Card>
        {editor}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {pendingBillTo}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Partes contractuales activas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {active.map((r) => (
              <div key={r.id} className="flex flex-wrap items-start justify-between gap-2 rounded-md border p-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-start gap-2">
                    <span className="min-w-0 break-words text-sm font-medium [overflow-wrap:anywhere]">{r.legalName}</span>
                    <span className="break-words text-xs text-muted-foreground [overflow-wrap:anywhere]">({r.partnerCode})</span>
                    {r.isPrimaryForRole && (
                      <Badge variant="secondary" className="shrink-0 text-xs">Principal</Badge>
                    )}
                  </div>
                  {r.notes && <p className="mt-0.5 break-words text-xs text-muted-foreground [overflow-wrap:anywhere]">{r.notes}</p>}
                </div>
                <Badge variant="outline" className="max-w-full shrink-0 whitespace-normal break-words">{ROLE_LABELS[r.role] ?? "Otra parte contractual"}</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      {editor}
    </div>
  );
}
